import type { Address } from "viem"
import { useCallback, useState } from "react"
import { format, fromUnixTime } from "date-fns"
import { observer } from "mobx-react-lite"
import { Link, useNavigate } from "react-router-dom"
import { getAddress, isAddress, isAddressEqual, zeroAddress } from "viem"
import { useAccount, useConfig } from "wagmi"

import { BackLink } from "@/components/back-link"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { Input } from "@/components/input"
import { SuperVoteLogo } from "@/components/super-vote-logo"
import SuperVoterAccessDialog from "@/components/super-voter/super-voter-access-dialog"
import SuperVoterInfoDialog from "@/components/super-voter/super-voter-info-dialog"
import { Compound as Table } from "@/components/table"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ForceRefreshButton } from "@/domains/common/components/force-refresh-button"
import { Lozenge } from "@/domains/common/components/lozenge"
import { MessageBox } from "@/domains/common/components/message-box"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { LcAddress } from "@/domains/core"
import { FeatureFlagsSettingsSection } from "@/domains/feature-flags/components/feature-flags-settings-section"
import { Cancel1Icon, CopyIcon, ExternalLinkIcon, PlusIcon, TickIcon } from "@/domains/icons"
import { delegateSuperVoterManagerInteraction, revokeSuperVoterForAllInteraction } from "@/domains/interactions"
import { cn, formatCurrency, formatPercentage } from "@/domains/library"
import { ApprovalModeSettingsSection } from "@/domains/settings/components/approval-mode-settings-section"
import { NotificationPositionSettingsSection } from "@/domains/settings/components/notification-position-settings-section"
// import { RpcSettingsSection } from "@/domains/settings/components/rpc-settings-section"
import { SettingsSectionTitle } from "@/domains/settings/components/settings-section-title"
import { SwapAggregatorsSectionWrapper } from "@/domains/settings/components/swap-aggregators-section-wrapper"
import { SwapSlippageSectionWrapper } from "@/domains/settings/components/swap-slippage-section-wrapper"
import { copyToClipboard } from "@/domains/theme/copy-to-clipboard"
import { useChainConfig } from "@/hooks/use-chain-config"
import useResponsive from "@/hooks/use-responsive"
import useScanner from "@/hooks/use-scanner"
import { useSuperVoterManagerAddress } from "@/hooks/use-super-voter-manager-address"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"

const SettingsPage = observer(() => {
  const navigate = useNavigate()
  const { isMobile } = useResponsive()

  const onBackLinkClick = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const title = "Settings"

  const backLink = isMobile ? null : <BackLink onBackLinkClick={onBackLinkClick} className="text-t100" />

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle
        title={title}
        subTitle="Configure your preferences and customize your user experience."
        middleEndItem={backLink}
      />

      <ManagerSettingsSection />

      <SuperVoterSettingsSection />

      <Card.Container className="mb-8 flex max-w-4xl flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 pb-8 text-t100 lg:p-6">
        <div className="relative text-t100">
          <SettingsSectionTitle title="General settings" />
          <div className="space-y-8">
            <ApprovalModeSettingsSection />
            <NotificationPositionSettingsSection />
          </div>
        </div>
      </Card.Container>

      {/* RPC Settings - Commented out until diagnostics page is built */}
      {/* <Card.Container className="mb-8 flex max-w-4xl flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 pb-8 text-t100 lg:p-6">
        <div className="relative text-t100">
          <SettingsSectionTitle title="RPC Settings" />
          <div className="space-y-8">
            <RpcSettingsSection />
          </div>
        </div>
      </Card.Container> */}

      <Card.Container className="card card-border mb-8 flex max-w-4xl flex-col gap-6 rounded-lg p-4 pb-8 text-t100 lg:p-6">
        <div className="relative text-t100">
          <SettingsSectionTitle title="Swap settings" />
          <div className="space-y-8">
            <SwapAggregatorsSectionWrapper />
            <SwapSlippageSectionWrapper />
          </div>
        </div>
      </Card.Container>

      <FeatureFlagsSettingsSection />
    </PageContainer>
  )
})

const ManagerSettingsSection = observer(() => {
  const scaleApp = useScaleApplication()
  const vestedToken = useVestedToken()
  const account = useAccount()
  const [newAddress, setNewAddress] = useState("")
  const [newName, setNewName] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [editingName, setEditingName] = useState<LcAddress | null>(null)
  const [editingNameValue, setEditingNameValue] = useState("")
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [importText, setImportText] = useState("")
  const [importError, setImportError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  if (!scaleApp || !account.address) {
    return null
  }

  const managerStore = scaleApp.Manager.Store
  const managerAddresses = managerStore.managerAddresses
  const selectedManagerAddress = managerStore.selectedManagerAddress

  // Get approval status from store (polled on-chain every minute)
  const isManagerApproved = (managerAddress: LcAddress): boolean => {
    return managerStore.getManagerApprovalStatus(managerAddress)
  }

  const handleAddAddress = () => {
    setError(null)
    if (!LcAddress.isValid(newAddress)) {
      setError("Invalid address")
      return
    }

    const address = new LcAddress(newAddress)
    if (managerAddresses.some((addr) => addr.equals(address))) {
      // Update existing address name
      managerStore.setManagerAddressName(address, newName.trim())
      setNewAddress("")
      setNewName("")
      return
    }

    managerStore.addManagerAddress(address, newName.trim() || undefined)
    setNewAddress("")
    setNewName("")
  }

  const handleStartEditName = (address: LcAddress) => {
    setEditingName(address)
    setEditingNameValue(managerStore.getManagerAddressName(address) || "")
  }

  const handleSaveName = (address: LcAddress) => {
    managerStore.setManagerAddressName(address, editingNameValue.trim())
    setEditingName(null)
    setEditingNameValue("")
  }

  const handleCancelEditName = () => {
    setEditingName(null)
    setEditingNameValue("")
  }

  const handleRemoveAddress = (address: LcAddress) => {
    managerStore.removeManagerAddress(address)
  }

  const handleSelectAddress = (address: LcAddress | null) => {
    managerStore.setSelectedManagerAddress(address)
  }

  const handleExport = async () => {
    const exportData = managerAddresses.map((address) => ({
      address: address.toJSON(),
      name: managerStore.getManagerAddressName(address),
    }))
    const jsonString = JSON.stringify(exportData, null, 2)
    await copyToClipboard(jsonString)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleImport = () => {
    setImportError(null)
    try {
      const parsed = JSON.parse(importText) as Array<{ address: string; name?: string }>
      if (!Array.isArray(parsed)) {
        setImportError("Invalid format: expected an array of addresses")
        return
      }

      let importedCount = 0
      for (const item of parsed) {
        if (!item.address || !LcAddress.isValid(item.address)) {
          continue
        }
        const address = new LcAddress(item.address)
        if (!managerAddresses.some((addr) => addr.equals(address))) {
          managerStore.addManagerAddress(address, item.name)
          importedCount++
        } else if (item.name) {
          // Update name if address already exists
          managerStore.setManagerAddressName(address, item.name)
        }
      }

      if (importedCount === 0 && parsed.length > 0) {
        setImportError("No new addresses to import (all addresses already exist)")
        return
      }

      setShowImportDialog(false)
      setImportText("")
    } catch (error) {
      setImportError("Invalid JSON format. Please check your input.")
    }
  }

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText()
      setImportText(text)
      setShowImportDialog(true)
    } catch (error) {
      setImportError("Failed to read from clipboard. Please paste manually.")
      setShowImportDialog(true)
    }
  }

  // Get NFTs for selected manager address
  const selectedAddress = selectedManagerAddress ?? null
  const managerLocks = selectedAddress ? scaleApp.Manager.Lock.Repository.getAll() : []

  return (
    <Card.Container className="card card-border mb-8 flex max-w-4xl flex-col gap-6 rounded-lg p-4 pb-8 text-t100 lg:p-6">
      <div className="relative text-t100">
        <div className="mb-4 flex items-start justify-between">
          <SettingsSectionTitle
            title="Managed Addresses"
            subtitle="Manage addresses that have delegated powers to you"
          />
          <div className="flex gap-2">
            <Button variant="neutral" size="sm" onClick={handleExport} className="flex items-center gap-2">
              <CopyIcon className="h-4 w-4" />
              {copied ? "Copied!" : "Export"}
            </Button>
            <Button variant="neutral" size="sm" onClick={handlePasteClick} className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              Import
            </Button>
          </div>
        </div>
        <div className="space-y-6">
          {/* Add new managed address */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-t100">Add Managed Address</label>
            <div className="flex max-w-2xl flex-col gap-2">
              <div className="flex items-center gap-4">
                <Input
                  value={newAddress}
                  onChange={(e) => {
                    setNewAddress(e.target.value)
                    setError(null)
                    // Pre-fill name if address already exists
                    if (LcAddress.isValid(e.target.value)) {
                      const address = new LcAddress(e.target.value)
                      const existingName = managerStore.getManagerAddressName(address)
                      if (existingName) {
                        setNewName(existingName)
                      }
                    }
                  }}
                  placeholder="0x..."
                  className="flex-1 rounded-xl bg-p300 pl-4"
                />
                <Button variant="primary" onClick={handleAddAddress}>
                  {LcAddress.isValid(newAddress) &&
                  managerAddresses.some((addr) => addr.equals(new LcAddress(newAddress)))
                    ? "Update"
                    : "Add"}
                </Button>
              </div>
              <Input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value)
                  setError(null)
                }}
                placeholder="Wallet name (optional, e.g., 'Friend's wallet', 'Trading wallet')"
                className="flex-1 rounded-xl bg-p300 pl-4"
              />
            </div>
            {error && <div className="text-error text-sm">{error}</div>}
          </div>

          {/* Managed addresses list */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-t100">Managed Addresses</label>
            {managerAddresses.length === 0 ? (
              <div className="bg-p300 text-sm text-t200">No managed addresses added</div>
            ) : (
              <Table.Root>
                <Table.Body>
                  {managerAddresses.map((address) => {
                    const isApproved = isManagerApproved(address)
                    const isSelected = selectedManagerAddress?.equals(address) ?? false
                    return (
                      <Table.Tr
                        key={address.toString()}
                        className={`cursor-pointer ${isSelected ? "bg-p300-light" : "bg-p300"}`}
                        onClick={() => handleSelectAddress(address)}
                      >
                        {/* Name and Address Column */}
                        <Table.Td>
                          <div className="flex flex-col gap-1">
                            {editingName?.equals(address) ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editingNameValue}
                                  onChange={(e) => setEditingNameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveName(address)
                                    } else if (e.key === "Escape") {
                                      handleCancelEditName()
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-8 rounded bg-p400 px-2 text-sm"
                                  autoFocus
                                />
                                <Button
                                  variant="neutral"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleSaveName(address)
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="neutral"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCancelEditName()
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div
                                className="text-sm font-medium text-t100"
                                onDoubleClick={(e) => {
                                  e.stopPropagation()
                                  handleStartEditName(address)
                                }}
                              >
                                {managerStore.getManagerAddressName(address) || "Unnamed wallet"}
                              </div>
                            )}
                            <div className="font-mono text-xs text-t200">{address.toString()}</div>
                          </div>
                        </Table.Td>
                        {/* Approval Status Column */}
                        <Table.Td>
                          <div className="flex items-center justify-center">
                            {isApproved ? (
                              <Lozenge color="success" size="xxs" contentLeft={<TickIcon size={12} />}>
                                Delegated
                              </Lozenge>
                            ) : (
                              <Lozenge color="error" size="xxs" contentLeft={<Cancel1Icon size={12} />}>
                                Disconnected
                              </Lozenge>
                            )}
                          </div>
                        </Table.Td>
                        {/* Remove Button Column */}
                        <Table.Td>
                          <div className="flex items-center justify-center">
                            <Button
                              variant="neutral"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveAddress(address)
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                  {/* Use connected wallet option */}
                  <Table.Tr
                    className={`cursor-pointer ${selectedManagerAddress === null ? "bg-p300-light" : "bg-p300"}`}
                    onClick={() => handleSelectAddress(null)}
                  >
                    <Table.Td>
                      <span className="text-sm font-medium text-t100">Use connected wallet</span>
                    </Table.Td>
                    <Table.Td></Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>
                </Table.Body>
              </Table.Root>
            )}
          </div>

          {/* NFTs for selected address */}
          {selectedAddress && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-t100">
                  Managing NFTs for {managerStore.getManagerAddressName(selectedAddress) || selectedAddress.truncate()}
                </label>
                <ForceRefreshButton query={scaleApp.Manager.Lock.Orchestrator.query} title="Refresh NFTs" />
              </div>
              {managerLocks.length === 0 ? (
                <div className="text-sm text-t200">No NFTs found for this address</div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {managerLocks.map((lock) => (
                    <div key={lock.id} className="flex flex-col gap-2 rounded-xl border border-p400-light bg-p300 p-3">
                      <div className="font-medium text-t100">#{lock.id}</div>
                      <div className="text-xs text-t200">
                        {formatCurrency(lock.lockValue, 4)} {vestedToken.symbol}
                      </div>
                      {lock.lockEnds > 0 && (
                        <div className="text-xs text-t200">{format(fromUnixTime(lock.lockEnds), "MMM do, yyyy")}</div>
                      )}
                      <div className="flex h-[20px] items-center overflow-hidden rounded-xl border-2 border-p200-light bg-p200">
                        <div
                          className="flex h-full items-center justify-center rounded-l-xl rounded-r-sm bg-[#1a56cd] px-2 text-center text-[10px] text-white"
                          style={{ width: `${Math.min((lock.power || 0) * 100, 100)}%` }}
                        >
                          {lock.power > 0 && formatPercentage(lock.power, 2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-2xl rounded-lg bg-p400 p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-t100">Import Managed Addresses</h3>
              <button
                onClick={() => {
                  setShowImportDialog(false)
                  setImportText("")
                  setImportError(null)
                }}
                className="text-t300 hover:text-t100"
              >
                ✕
              </button>
            </div>

            <p className="mb-4 text-sm text-t200">
              Paste the exported JSON to import managed addresses. Existing addresses will have their names updated.
            </p>

            {/* Error Message */}
            {importError && (
              <div className="mb-4 rounded border border-alert-700 bg-alert-900 p-3 text-sm text-alert-500">
                {importError}
              </div>
            )}

            {/* JSON Input */}
            <div className="mb-4">
              <label className="mb-2 block text-sm text-t200">Paste JSON</label>
              <textarea
                value={importText}
                onChange={(e) => {
                  setImportText(e.target.value)
                  setImportError(null)
                }}
                placeholder={`[\n  {\n    "address": "0x...",\n    "name": "Wallet name"\n  }\n]`}
                className="h-64 w-full rounded border border-p400-light bg-p400 p-4 font-mono text-sm text-t200 outline-none focus:border-p400-light"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="neutral"
                onClick={() => {
                  setShowImportDialog(false)
                  setImportText("")
                  setImportError(null)
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleImport} disabled={!importText.trim()}>
                Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card.Container>
  )
})

const SuperVoterSettingsSection = observer(() => {
  const account = useAccount()
  const config = useConfig()
  const scanner = useScanner()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()
  const { currentManagerAddress, superVoterUserManagerOfQuery } = useSuperVoterManagerAddress({
    account: account.address ?? zeroAddress,
  })
  const [manageAddress, setManageAddress] = useState<string>("")
  const [isSuperVoterDialogOpen, setIsSuperVoterDialogOpen] = useState(false)
  const [isSuperVoterInfoDialogOpen, setIsSuperVoterInfoDialogOpen] = useState(false)

  if (!account.address || !scaleApp) {
    return null
  }

  const hasSuperVoterAccess = scaleApp.Pages.Vote.Store.hasSuperVoterAccess

  const hasManager = !isAddressEqual(currentManagerAddress ?? zeroAddress, zeroAddress)

  const onDelegateVotesClick = async () => {
    if (!manageAddress) {
      emitter.emit("error-delegating-votes", {
        title: "Delegate votes failed",
        description: `Please set a manager address`,
        variant: "error",
      })
      return
    }

    if (!isAddress(manageAddress)) {
      emitter.emit("error-delegating-votes", {
        title: "Invalid manager address",
        description: `Please enter a valid address`,
        variant: "error",
      })
      return
    }

    if (!account.address) {
      emitter.emit("error-delegating-votes", {
        title: "Delegate votes failed",
        description: `Account not available, do you have a wallet connected?`,
        variant: "error",
      })
      return
    }

    const result = await delegateSuperVoterManagerInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      superVoterAddress: chainConfig.equalizer.contracts.superVoter,
      managerAddress: getAddress(manageAddress),
      emitter,
      eventBus: scaleApp.EventBus,
    })

    if (result.success) {
      void superVoterUserManagerOfQuery.refetch()
      setManageAddress("")
    }
  }

  const onResetDelegateVotesClick = async () => {
    if (!account.address) {
      emitter.emit("error-delegating-votes", {
        title: "Delegate votes failed",
        description: `Account not available, do you have a wallet connected?`,
        variant: "error",
      })
      return
    }

    const result = await delegateSuperVoterManagerInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      superVoterAddress: chainConfig.equalizer.contracts.superVoter,
      managerAddress: getAddress(zeroAddress),
      emitter,
      eventBus: scaleApp.EventBus,
    })

    if (result.success) {
      void superVoterUserManagerOfQuery.refetch()
      setManageAddress("")
    }
  }

  const onRevokeSuperVoterAccessClick = async () => {
    try {
      if (!account.address) {
        return false
      }
      const result = await revokeSuperVoterForAllInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        vestedAddress: chainConfig.tokens.vested.address,
        superVoterAddress: chainConfig.equalizer.contracts.superVoter,
        emitter,
        eventBus: scaleApp.EventBus,
      })

      if (result.success) {
        void superVoterUserManagerOfQuery.refetch()
      }

      console.log("onRevokeSuperVoterAccessClick", result)
    } catch (error) {
      console.error("onRevokeSuperVoterAccessClick", error)
    }
  }

  return (
    <div className="mb-8 space-y-8">
      <SuperVoterAccessDialog isOpen={isSuperVoterDialogOpen} setIsOpen={setIsSuperVoterDialogOpen} />
      <SuperVoterInfoDialog isOpen={isSuperVoterInfoDialogOpen} setIsOpen={setIsSuperVoterInfoDialogOpen} />

      {/* Delegate Your Votes Section */}
      <Card.Container className="card card-border flex w-full max-w-4xl flex-col gap-6 rounded-lg p-4 pb-8 text-t100 lg:p-6">
        <div className="relative text-t100">
          <SettingsSectionTitle
            title="Delegate Your Votes"
            subtitle={
              <>
                <div className="flex items-center gap-0.5">
                  Delegate capabilities to a manager address with <SuperVoteLogo size="small" />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Lozenge color="success" size="xxs" contentLeft={<TickIcon size={12} />}>
                    Vote
                  </Lozenge>
                  <Lozenge color="success" size="xxs" contentLeft={<TickIcon size={12} />}>
                    Max lock NFTs
                  </Lozenge>
                  <Lozenge color="error" size="xxs" contentLeft={<TickIcon size={12} />}>
                    Claim rewards
                  </Lozenge>
                  <Lozenge color="error" size="xxs" contentLeft={<Cancel1Icon size={12} />}>
                    Transfer
                  </Lozenge>
                  <Lozenge color="error" size="xxs" contentLeft={<Cancel1Icon size={12} />}>
                    Withdraw
                  </Lozenge>
                </div>
              </>
            }
          />

          {/* When they already have a manager */}
          <ShowWhenTrue when={hasManager}>
            <MessageBox variant="info" className="mb-4 max-w-2xl">
              <div>
                Your current manager is
                <br />
                <a
                  href={scanner.makeAddressUrl(currentManagerAddress ?? "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono"
                >
                  {currentManagerAddress}
                </a>
              </div>
            </MessageBox>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Input
                variant="p300"
                placeholder="Enter manager address"
                value={manageAddress}
                onChange={(e) => setManageAddress(e.target.value)}
                className="flex-1 rounded-xl"
              />
              <Button variant="primary" onClick={onDelegateVotesClick} className="whitespace-nowrap">
                Delegate
              </Button>
              <Button variant="neutral" onClick={onResetDelegateVotesClick} className="whitespace-nowrap">
                Reset
              </Button>
            </div>
          </ShowWhenTrue>

          {/* When they have not assigned a manager yet */}
          <ShowWhenTrue when={!hasManager}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Input
                variant="p300"
                placeholder="Enter manager address"
                value={manageAddress}
                onChange={(e) => setManageAddress(e.target.value)}
                className="flex-1 rounded-xl"
              />
              <Button variant="primary" onClick={onDelegateVotesClick} className="whitespace-nowrap">
                Delegate
              </Button>
            </div>
          </ShowWhenTrue>
        </div>
      </Card.Container>

      {/* SuperVoter Access Section */}
      <Card.Container className="card card-border flex w-full max-w-4xl flex-col gap-6 rounded-lg p-4 pb-8 text-t100 lg:p-6">
        <div className="relative text-t100">
          <div className="mb-4 flex items-center gap-2">
            <SuperVoteLogo />
            <ForceRefreshButton query={scaleApp.Pages.Vote.Orchestrator.query} title="Refresh Access" />
          </div>

          <div className="mt-4 flex flex-col gap-4 sm:grid sm:grid-cols-[25%_75%]">
            <span className="text-t200">Contract address:</span>
            <a
              href={scanner.makeAddressUrl(chainConfig.equalizer.contracts.superVoter)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-2 font-mono text-sm"
            >
              {chainConfig.equalizer.contracts.superVoter}
              <ExternalLinkIcon size={16} className="cursor-pointer text-t200" />
            </a>
            <span className="text-t200">Permission:</span>
            <div className="flex items-center justify-start gap-2">
              <Button variant="neutral" onClick={() => setIsSuperVoterInfoDialogOpen(true)}>
                Info
              </Button>
              <ShowWhenTrue when={Boolean(hasSuperVoterAccess)}>
                <Lozenge color="error" size="md" className="cursor-pointer" onClick={onRevokeSuperVoterAccessClick}>
                  Revoke
                </Lozenge>
              </ShowWhenTrue>
              <ShowWhenTrue when={!hasSuperVoterAccess}>
                <Button variant="primary" onClick={() => setIsSuperVoterDialogOpen(true)}>
                  Grant Access
                </Button>
              </ShowWhenTrue>
            </div>
          </div>
        </div>
      </Card.Container>
    </div>
  )
})

export default SettingsPage
